import { rootApi } from '../rootApi';

export const technicalSupportRequestApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTechnicalSupportVehicleModelYearByChassisNo: builder.query({
      query: (chassisNo) => ({
        url: 'v1/vehicledetailinformation/getmodelyearbychassisno',
        method: 'GET',
        params: { ChassisNo: chassisNo }
      }),
      transformResponse: (response) => response?.data ?? null
    }),
    getTechnicalSupportRequestPreviousKm: builder.query({
      query: ({ chassisNo, menzilType, excludeRequestId }) => ({
        url: 'v1/TechnicalSupportRequest/PreviousKm',
        method: 'GET',
        params: {
          ChassisNo: chassisNo,
          MenzilType: menzilType,
          ...(excludeRequestId != null ? { ExcludeRequestId: excludeRequestId } : {})
        }
      })
    }),

    getTechnicalSupportRequests: builder.query({
      query: (params = {}) => {
        const {
          activeOnly = 1,
          pageNumber = 1, 
          pageSize = 10,
          languageId = 1,
          dealerId
        } = params;

        const hasValue = (value) => value !== null && value !== undefined && value !== '';

        const queryParams = {
          IsActive: activeOnly,
          PageNumber: pageNumber,
          PageSize: pageSize,
          LanguageId: languageId,
          ...(hasValue(dealerId) ? { DealerId: dealerId } : {})
        };

        const optionalFields = [
          'TicketNo',
          'ErrorNotificationDate',
          'CreatedDate',
          'ChassisNo',
          'VehicleModelEmissions',
          'DealerName',
          'Country',
          'UserNameSurname',
          'ErrorDescription',
          'MainSystemCode',
          'SubSystemCode',
          'RegionalManagerName',
          'TechnicalResponsibleName',
          'ErrorEffect',
          'Status',
          'IsVor',
          'StatusNotificationInf'
        ];

        optionalFields.forEach((field) => {
          if (hasValue(params[field])) {
            queryParams[field] = params[field];
          }
        });

        if (Array.isArray(params.filters) && params.filters.length > 0) {
          params.filters.forEach((filter, index) => {
            queryParams[`Filters[${index}].Column`] = filter?.column;
            queryParams[`Filters[${index}].Operator`] = filter?.operator;
            queryParams[`Filters[${index}].Value`] = filter?.value;
          });
        }

        if (Array.isArray(params.sorts) && params.sorts.length > 0) {
          params.sorts.forEach((sort, index) => {
            queryParams[`Sorts[${index}].Column`] = sort?.column;
            queryParams[`Sorts[${index}].Direction`] = sort?.direction;
          });
        }

        return {
          url: 'v1/technicalsupportrequest',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => {
        const items = response?.data?.items || [];
        return {
          items, 
          totalCount: response?.data?.totalCount || 0,
          isSuccessful: response?.isSuccessful || false,
          errorMessage: response?.errorMessage || null
        };
      },
      providesTags: ['TechnicalSupportRequest']
    }),

    getTechnicalSupportRequestHistory: builder.query({
      query: ({ id, languageId }) => ({
        url: `v1/TechnicalSupportRequest/${id}/history`,
        method: 'GET',
        params: { languageId }
      }),
      transformResponse: (response) => response?.data?.items ?? response?.data ?? []
    }),

    getTechnicalSupportResponsibles: builder.query({
      query: (siteUseId) => ({
        url: 'v1/TechnicalSupportRequest/GetResponsibles',
        method: 'GET',
        params: { siteUseId }
      }),
      transformResponse: (response) => (Array.isArray(response?.data) ? response.data : response?.data?.items || [])
    }),

    getVor: builder.query({
      query: (params = {}) => {
        const {
          activeOnly = 1,
          pageNumber = 1, 
          pageSize = 10,
          languageId = 1
        } = params;

        return {
          url: 'v1/technicalsupportrequest/getallvor',
          method: 'GET',
          params: {
            IsActive: activeOnly,
            PageNumber: pageNumber,
            PageSize: pageSize,
            LanguageId: languageId
          }
        };
      },
      transformResponse: (response) => {
        const items = response?.data?.items || [];
        return {
          items, 
          totalCount: response?.data?.totalCount || 0,
          isSuccessful: response?.isSuccessful || false,
          errorMessage: response?.errorMessage || null
        };
      },
      providesTags: ['Vor']
    }),

    createTechnicalSupportRequest: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        const onsiteSupportSource = body?.OnsiteSupportList ?? body?.onSiteSupportReason ?? [];
        const declarationControlSource = body?.DeclarationControlList ?? body?.technicalServiceDeclaration ?? [];
        const onsiteSupportList = (Array.isArray(onsiteSupportSource) ? onsiteSupportSource : [])
          .map((item) => Number(item))
          .filter(Number.isFinite);
        const declarationControlList = (Array.isArray(declarationControlSource) ? declarationControlSource : [])
          .map((item) => Number(item))
          .filter(Number.isFinite);

        return {
          url: 'v1/technicalsupportrequest',
          method: 'POST',
          body: {
            ...body,
            OnsiteSupportList: onsiteSupportList,
            DeclarationControlList: declarationControlList,
            createdName: body?.createdName ?? 'System',
            createdSession: body?.createdSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    updateTechnicalSupportRequest: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        const onsiteSupportSource = body?.OnsiteSupportList ?? body?.onSiteSupportReason ?? [];
        const declarationControlSource = body?.DeclarationControlList ?? body?.technicalServiceDeclaration ?? [];
        const onsiteSupportList = (Array.isArray(onsiteSupportSource) ? onsiteSupportSource : [])
          .map((item) => Number(item))
          .filter(Number.isFinite);
        const declarationControlList = (Array.isArray(declarationControlSource) ? declarationControlSource : [])
          .map((item) => Number(item))
          .filter(Number.isFinite);
        return {
          url: 'v1/technicalsupportrequest',
          method: 'PATCH',
          body: {
            ...body,
            OnsiteSupportList: onsiteSupportList,
            DeclarationControlList: declarationControlList,
            modifiedName: body?.modifiedName ?? 'System',
            lastUpdateSession: body?.lastUpdateSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    deleteTechnicalSupportRequest: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        return {
          url: 'v1/technicalsupportrequest',
          method: 'DELETE',
          body: {
            ...body,
            modifiedName: body?.modifiedName ?? 'System',
            lastUpdateSession: body?.lastUpdateSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    addComment: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        return {
          url: 'v1/technicalsupportrequest/AddComment',
          method: 'POST',
          body: {
            ...body,
            createdName: body?.createdName ?? 'System',
            createdSession: body?.createdSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    deleteComment: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        return {
          url: 'v1/technicalsupportrequest/DeleteComment',
          method: 'DELETE',
          body: {
            ...body,
            modifiedName: body?.modifiedName ?? 'System',
            lastUpdateSession: body?.lastUpdateSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    updateCommentReadStatus: builder.mutation({
      query: (request) => {
        const { ...body } = request;
        return {
          url: 'v1/technicalsupportrequest/UpdateCommentReadStatus',
          method: 'PATCH',
          body: {
            ...body,
            modifiedName: body?.modifiedName ?? 'System',
            lastUpdateSession: body?.lastUpdateSession ?? 'web'
          }
        };
      },
      invalidatesTags: ['TechnicalSupportRequest']
    }),

    // Document Management endpoints
    uploadTechnicalSupportRequestDocument: builder.mutation({
      query: ({ TechnicalSupportRequestId, file, description }) => {
        const formData = new FormData();
        formData.append('TechnicalSupportRequestId', TechnicalSupportRequestId);
        formData.append('File', file);
        formData.append('Description', description || file?.name || 'attachment');
        return {
          url: 'v1/technicalsupportrequest/uploaddocument',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: (result, error, { TechnicalSupportRequestId }) => [
        { type: 'TechnicalSupportRequestDocument', id: TechnicalSupportRequestId }
      ]
    }),

    getTechnicalSupportRequestDocumentsById: builder.query({
      query: (id) => ({
        url: `v1/technicalsupportrequest/getdocumentsbyid?id=${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'TechnicalSupportRequestDocument', id }]
    }),

    deleteTechnicalSupportRequestDocument: builder.mutation({
      query: (data) => ({
        url: 'v1/technicalsupportrequest/DeleteDocument',
        method: 'DELETE',
        body: data
      }),
      invalidatesTags: (result, error, { Id }) => [
        { type: 'TechnicalSupportRequestDocument', id: Id }
      ]
    }),

    downloadAllTechnicalSupportRequestDocuments: builder.query({
      query: ({ technicalSupportRequestId, documentGroup }) => ({
        url: `v1/TechnicalSupportRequest/DownloadAllDocuments?TechnicalSupportRequestId=${technicalSupportRequestId}&DocumentGroup=${documentGroup}`,
        method: 'GET',
        responseHandler: async (response) => ({
          blob: await response.blob(),
          contentDisposition: response.headers.get('content-disposition')
        }),
        cache: 'no-cache'
      })
    }),

    // Comment Document Management endpoints
    uploadCommentDocument: builder.mutation({
      query: ({ CommentId, file, description }) => {
        const formData = new FormData();
        formData.append('TechnicalSupportRequestCommentId', CommentId);
        formData.append('File', file);
        formData.append('Description', description || file?.name || 'attachment');
        return {
          url: 'v1/technicalsupportrequest/UploadCommentDocument',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: (result, error, { CommentId }) => [
        { type: 'CommentDocument', id: CommentId }
      ]
    }),

    getCommentDocumentsById: builder.query({
      query: (id) => ({
        url: `v1/technicalsupportrequest/GetCommentDocumentsById?id=${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'CommentDocument', id }]
    }),

    deleteCommentDocument: builder.mutation({
      query: (data) => ({
        url: 'v1/technicalsupportrequest/DeleteCommentDocument',
        method: 'DELETE',
        body: data
      }),
      invalidatesTags: (result, error, { Id }) => [
        { type: 'CommentDocument', id: Id }
      ]
    }),

    downloadCommentDocuments: builder.mutation({
      query: ({ technicalSupportRequestId, documentIds }) => ({
        url: 'v1/TechnicalSupportRequest/DownloadCommentDocuments',
        method: 'POST',
        body: {
          technicalSupportRequestId,
          documentIds
        },
        responseHandler: async (response) => ({
          blob: await response.blob(),
          contentDisposition: response.headers.get('content-disposition')
        }),
        cache: 'no-cache'
      })
    }),
    uploadExpenseDocument: builder.mutation({
      query: ({ TechnicalSupportRequestId, file, description }) => {
        const formData = new FormData();
        formData.append('TechnicalSupportRequestId', TechnicalSupportRequestId);
        formData.append('File', file);
        formData.append('Description', description || file?.name || 'attachment');
        return {
          url: 'v1/technicalsupportrequest/UploadExpenseDocument',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: (result, error, { TechnicalSupportRequestId }) => [
        { type: 'ExpenseDocument', id: TechnicalSupportRequestId }
      ]
    }),
  
    getExpenseDocumentsById: builder.query({
      query: (id) => ({
        url: `v1/technicalsupportrequest/GetExpenseDocumentsById?id=${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'ExpenseDocument', id }]
    }),
  
    deleteExpenseDocument: builder.mutation({
      query: (data) => ({
        url: 'v1/technicalsupportrequest/DeleteExpenseDocument',
        method: 'DELETE',
        body: data
      }),
      invalidatesTags: (result, error, { Id }) => [
        { type: 'ExpenseDocument', id: Id }
      ]
    }),
    exportTechnicalSupportRequestToExcel: builder.mutation({  
      query: (body) => ({
        url: 'v1/technicalsupportrequest/ExportToExcel',
        method: 'POST',
        body: {
          filters: body.filters ?? null,
          sorts: body.sorts ?? null,
          compressionType: body.compressionType ?? 1,
        }
      }),
      transformResponse: (response) => {
        if (response?.isSuccessful && response?.data) {
          const { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType } = response.data;
          return { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType };
        }
        throw new Error(response?.errorMessage || response?.message || 'Export başarısız oldu');
      }
    }),
    getFeedbackQuestions: builder.query({
      query: (languageId) => ({
        url: `v1/technicalsupportrequest/feedback-questions?languageId=${languageId}`,
        method: 'GET'
      }),
      transformResponse: (response) => {
        return Array.isArray(response?.data) ? response.data : [];
      }
    }),

    saveFeedbackAnswers: builder.mutation({
      query: (body) => ({
        url: 'v1/technicalsupportrequest/feedback-answers',
        method: 'POST',
        body
      })
    }),

    getTechnicalSupportFeedbackFormReport: builder.query({
      query: (body) => ({
        url: 'v1/technicalsupportrequest/feedback-form-report',
        method: 'POST',
        body: {
          pageNumber: body?.pageNumber ?? 1,
          pageSize: body?.pageSize ?? 10,
          languageId: body?.languageId ?? 2,
          filters: body?.filters ?? null,
          sorts: body?.sorts ?? null,
          includeFilterOptions: body?.includeFilterOptions ?? false,
          filterOptionsColumns: body?.filterOptionsColumns ?? null
        }
      }),
      transformResponse: (response) => {
        if (response?.data && Array.isArray(response.data.items)) {
          return {
            items: response.data.items,
            totalCount: response.data.totalCount ?? 0,
            filterOptions: response.data.filterOptions ?? null
          };
        }
        return { items: [], totalCount: 0, filterOptions: null };
      },
      providesTags: ['TechnicalSupportFeedbackFormReport']
    }),

    exportTechnicalSupportFeedbackFormReportToExcel: builder.query({
      query: (body) => ({
        url: 'v1/technicalsupportrequest/feedback-form-report/exporttoexcel',
        method: 'POST',
        body: {
          filters: body?.filters ?? null,
          sorts: body?.sorts ?? null,
          compressionType: body?.compressionType ?? 0
        }
      }),
      transformResponse: (response) => {
        if (response?.isSuccessful && response?.data) {
          const { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType } = response.data;
          return { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType };
        }
        throw new Error(response?.errorMessage || response?.message || 'Export başarısız oldu');
      }
    }),

exportVorToExcel: builder.mutation({
      query: (body) => ({
        url: 'v1/technicalsupportrequest/vor/ExportToExcel',
        method: 'POST',
        body: {}
      }),
      transformResponse: (response) => {
        if (response?.isSuccessful && response?.data) {
          const { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType } = response.data;
          return { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType };
        }
        throw new Error(response?.errorMessage || response?.message || 'Export başarısız oldu');
      }
    }),
 
    exportDailyInactiveVehicleToExcel: builder.mutation({
      query: (body) => ({
        url: 'v1/technicalsupportrequest/vor/ExportDailyInactiveVehicleToExcel',
        method: 'POST',
        body: {}
      }),
      transformResponse: (response) => {
        if (response?.isSuccessful && response?.data) {
          const { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType } = response.data;
          return { fileBase64, fileName, contentType, recordCount, isCompressed, compressionType };
        }
        throw new Error(response?.errorMessage || response?.message || 'Export başarısız oldu');
      }
    }),

  })
});

export const {
  useLazyGetTechnicalSupportVehicleModelYearByChassisNoQuery,
  useGetTechnicalSupportRequestPreviousKmQuery,
  useGetTechnicalSupportRequestsQuery,
  useGetTechnicalSupportRequestHistoryQuery,
  useGetTechnicalSupportResponsiblesQuery,
  useCreateTechnicalSupportRequestMutation,
  useUpdateTechnicalSupportRequestMutation,
  useDeleteTechnicalSupportRequestMutation,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useUpdateCommentReadStatusMutation,
  useGetVorQuery,
  useUploadTechnicalSupportRequestDocumentMutation,
  useGetTechnicalSupportRequestDocumentsByIdQuery,
  useDeleteTechnicalSupportRequestDocumentMutation,
  useLazyDownloadAllTechnicalSupportRequestDocumentsQuery,
  useUploadCommentDocumentMutation,
  useGetCommentDocumentsByIdQuery,
  useDeleteCommentDocumentMutation,
  useDownloadCommentDocumentsMutation,
  useUploadExpenseDocumentMutation,
  useGetExpenseDocumentsByIdQuery,
  useDeleteExpenseDocumentMutation,
  useExportTechnicalSupportRequestToExcelMutation,
  useGetFeedbackQuestionsQuery,
  useSaveFeedbackAnswersMutation  ,
  useGetTechnicalSupportFeedbackFormReportQuery,
  useLazyExportTechnicalSupportFeedbackFormReportToExcelQuery,
 useExportVorToExcelMutation,          
  useExportDailyInactiveVehicleToExcelMutation
} = technicalSupportRequestApi;
